import { Box, Button, CircularProgress, Container, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ReplayIcon from "@mui/icons-material/Replay";

import "./otsPageLayout.css";
import ProgressComponent from "../../common/ProgressComponent";
import {
	useCheckOtsStatusQuery,
	useLazyGetOwnerTypesQuery,
	useLazyGetSocietyConfigQuery
} from "../../redux/reducers/settings.reducer";
import { IOwnerTypeDetails, ISocietyDetails } from "../../interfaces";
import { CONFIG_MODE_E, DEPLOYMENT_ERROR_MESSAGE, SYSTEM_INFO_MISSING_ERROR_MESSAGE } from "../../utils/constants";
import { useAppDispatch, useAppSelector } from "../../redux";
import { ReactComponent as ServiceOfflineImage } from "../../assets/images/service-offline.svg";
import { queries } from "../../redux/reducers/queries.reducer";
import { clearAuthState } from "../../redux/reducers/auth.reducer";

type RedirectionPromiseArrayType = [Promise<ISocietyDetails[]>, Promise<IOwnerTypeDetails[]>];

const DEVICE_REGISTRATION_PATH = "device-registration";
const SOCIETY_REGISTRATION_PATH = "property-registration";
const SOCIETY_CONFIGURATION_PATH = "property-configuration";
const CAMERAS_LIST_PATH = "cameras-list";
const ADD_CAMERA_PATH = "add-camera";
const OTS_COMPLETE_PATH = "finalize";
const MYGATE_DEVICE_SETUP_PATH = "mygate-device-setup";
const CUSTOM_FIELDS_CONFIG_PATH = "custom-fields";

const OTSPageLayout: FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);

	const loadingTimer = useRef<NodeJS.Timeout | null>(null);

	const [showLoader, setShowLoader] = useState<boolean>(true);
	const [showServiceStartingMessage, setShowServiceStartingMessage] = useState<boolean>(false);
	const [retryMessage, setRetryMessage] = useState<string>("");

	// APIS
	const {
		data: siteConfig,
		isLoading: getSiteConfigLoading,
		isFetching: getSiteConfigFetching,
		error: getSiteConfigError,
		refetch: refetchSiteConfig
	} = useCheckOtsStatusQuery(null, {
		refetchOnMountOrArgChange: true,
		skip: !authState.user.token || authState.user.token === "invalid"
	});
	const [getSocietyConfig] = useLazyGetSocietyConfigQuery();
	const [getOwnerTypes] = useLazyGetOwnerTypesQuery();

	const currentOTSPath = location.pathname.split("/ots/")[1];

	function handleRetry() {
		setRetryMessage("");
		setShowServiceStartingMessage(false);
		refetchSiteConfig();
	}

	function clearAllApiStates() {
		dispatch(queries.util.resetApiState());
	}

	function getOTSScreenHeader(): string {
		if (currentOTSPath === "") return "Select Your Integration";
		if (currentOTSPath === DEVICE_REGISTRATION_PATH) return "Device Registration";
		if (currentOTSPath === SOCIETY_REGISTRATION_PATH) return "Property Details";
		if (currentOTSPath === SOCIETY_CONFIGURATION_PATH) return "Property Configuration";
		if (currentOTSPath === ADD_CAMERA_PATH) return "Add Camera";
		if (currentOTSPath === CAMERAS_LIST_PATH) return "List of Cameras";
		if (currentOTSPath === MYGATE_DEVICE_SETUP_PATH) return "Device Setup";
		if (currentOTSPath === CUSTOM_FIELDS_CONFIG_PATH) return "Custom Fields";

		return "One Time Setup";
	}

	function getOTSProgress(): number {
		const configMode =
			location.state && typeof location.state.mode === "number"
				? (location.state.mode as CONFIG_MODE_E)
				: CONFIG_MODE_E.GATE_GUARD;

		if (currentOTSPath === "") return 0;

		if (configMode === CONFIG_MODE_E.GATE_GUARD) {
			if (currentOTSPath === DEVICE_REGISTRATION_PATH) return 0;
			if (currentOTSPath === SOCIETY_REGISTRATION_PATH) return Math.floor((1 / 6) * 100);
			if (currentOTSPath === SOCIETY_CONFIGURATION_PATH) return Math.floor((2 / 6) * 100);
			if (currentOTSPath === CUSTOM_FIELDS_CONFIG_PATH) return Math.floor((3 / 6) * 100);
			if (currentOTSPath === ADD_CAMERA_PATH) return Math.floor((3 / 6) * 100);
			if (currentOTSPath === CAMERAS_LIST_PATH) return Math.floor((4 / 6) * 100);
		} else if (configMode === CONFIG_MODE_E.MY_GATE) {
			if (currentOTSPath === MYGATE_DEVICE_SETUP_PATH) return 0;
			if (currentOTSPath === DEVICE_REGISTRATION_PATH) return Math.floor((1 / 5) * 100);
			if (currentOTSPath === SOCIETY_REGISTRATION_PATH) return Math.floor((2 / 5) * 100);
			if (currentOTSPath === ADD_CAMERA_PATH) return Math.floor((3 / 5) * 100);
			if (currentOTSPath === CAMERAS_LIST_PATH) return Math.floor((4 / 5) * 100);
		}

		if (currentOTSPath === OTS_COMPLETE_PATH) return 99;

		return 0;
	}

	const handleGetSocietyConfig = useCallback((): Promise<ISocietyDetails[]> => {
		return new Promise((resolve) => {
			getSocietyConfig()
				.unwrap()
				.then((response) => resolve(response.results))
				.catch((error) => {
					console.error("GET SOCIETY CONFIG ERROR ::", error);
					resolve([]);
				});
		});
	}, [getSocietyConfig]);

	const handleGetOwnersList = useCallback((): Promise<IOwnerTypeDetails[]> => {
		return new Promise((resolve) => {
			getOwnerTypes()
				.unwrap()
				.then((response) => resolve(response.results))
				.catch((error) => {
					console.error("GET OWNER TYPES ERROR ::", error);
					resolve([]);
				});
		});
	}, [getOwnerTypes]);

	const handleRedirection = useCallback(() => {
		/**
		 * If the user token is not present in redux store, it means that the user is not logged in.
		 * So, navigate to login screen
		 */
		if (!authState.user.token || authState.user.token === "invalid") {
			navigate("/login");
			return;
		}

		/**
		 * If site config is not present, then the API returned an error.
		 */
		if (!siteConfig) {
			return;
		}

		if (loadingTimer.current) {
			clearTimeout(loadingTimer.current);
			loadingTimer.current = null;
			setShowServiceStartingMessage(false);
		}

		const promiseArray: RedirectionPromiseArrayType = [handleGetSocietyConfig(), handleGetOwnersList()];

		Promise.all(promiseArray).then((response) => {
			const societiesList = response[0];
			const ownerTypesList = response[1];

			/**
			 * The current active screen is /ots (GateGuard or MyGate mode selection).
			 */
			if (!currentOTSPath) {
				/**
				 * If the mode is not set in database (null), then stay on the current screen.
				 */
				if (siteConfig && (typeof siteConfig.mode !== "number" || siteConfig.mode < 0)) {
					setShowLoader(false);
					return;
				}
			}

			/**
			 * The current active screen is not /ots (GateGuard or MyGate mode selection).
			 *
			 * If the owner types list is empty & current active screen is not device registration,
			 * then navigate to device registration screen.
			 */
			if (ownerTypesList.length <= 0) {
				if (currentOTSPath !== DEVICE_REGISTRATION_PATH) {
					navigate("/ots/device-registration");
				}
				setShowLoader(false);
				return;
			}

			/**
			 * If the societies list is empty & current active screen is not device registration,
			 * then navigate to device registration screen.
			 *
			 * There is no way to detect if the user has selected manual registration or not,
			 * so, after navigating to device registration screen, user will have to start the sync again.
			 */
			if (societiesList.length <= 0) {
				if (currentOTSPath !== DEVICE_REGISTRATION_PATH) {
					navigate("/ots/device-registration");
				}
				setShowLoader(false);
				return;
			}

			/**
			 * Everything needed for the OTS is present, so navigate to login screen.
			 */
			navigate("/dashboard");
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteConfig]);

	useEffect(() => {
		if (!authState.user.token || authState.user.token === "invalid") {
			navigate("/login");
			clearAllApiStates();
		} else if (siteConfig) {
			if (typeof siteConfig.mode !== "number" || siteConfig.mode < 0) {
				navigate("/ots");
				setShowLoader(false);
			} else {
				handleRedirection();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState.user.token, handleRedirection, navigate, siteConfig]);

	useEffect(() => {
		if (getSiteConfigLoading || getSiteConfigFetching) {
			if (!loadingTimer.current) {
				loadingTimer.current = setTimeout(() => {
					setShowServiceStartingMessage(true);
					loadingTimer.current = null;
				}, 5 * 1000);
			}
		}
	}, [getSiteConfigFetching, getSiteConfigLoading]);

	useEffect(() => {
		if (getSiteConfigError) {
			if ("status" in getSiteConfigError && getSiteConfigError.status === 401) {
				dispatch(clearAuthState());
				clearAllApiStates();
			} else if ("data" in getSiteConfigError && getSiteConfigError.data === DEPLOYMENT_ERROR_MESSAGE) {
				setRetryMessage(DEPLOYMENT_ERROR_MESSAGE);
			} else if ("data" in getSiteConfigError && getSiteConfigError.data === SYSTEM_INFO_MISSING_ERROR_MESSAGE) {
				setRetryMessage(SYSTEM_INFO_MISSING_ERROR_MESSAGE);
			} else {
				setTimeout(() => {
					refetchSiteConfig();
				}, 5 * 1000);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getSiteConfigError]);

	if (showLoader) {
		if (retryMessage) {
			return (
				<Box className="service-offline-message-wrapper">
					<ServiceOfflineImage width="400px" />

					<Typography variant="h5">{retryMessage}</Typography>

					<Button disableElevation variant="contained" color="primary" startIcon={<ReplayIcon />} onClick={handleRetry}>
						Retry
					</Button>
				</Box>
			);
		}

		if (showServiceStartingMessage) {
			return (
				<Box className="service-offline-message-wrapper">
					<ServiceOfflineImage width="400px" />

					<CircularProgress />

					<Typography variant="h5">
						Please wait, the login page will be ready once the backend services are up.
					</Typography>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					right: 0,
					bottom: 0,
					left: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					zIndex: 9999
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ height: "100vh", width: "100vw" }}>
			<Box className="ots-screen-wrapper">
				<Box className="ots-screen-header-wrapper">
					<Typography variant="h4" color="inherit" fontWeight={500}>
						{getOTSScreenHeader()}
					</Typography>
				</Box>

				<Container maxWidth="xl" classes={{ root: "ots-screen-content-wrapper" }}>
					<Box className="ots-screen-content-section">
						<ProgressComponent value={getOTSProgress()} progressText="One Time Setup Progress" />

						<Outlet />
					</Box>
				</Container>
			</Box>
		</Box>
	);
};

export default OTSPageLayout;
